/** TicketDetallePage — Detalle D-B "Sidebar Drawer". Pixel-perfect del mockup. */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, Send, Paperclip, Loader2, X, FileText, Download, ChevronRight,
  ChevronDown, ChevronUp,
  Mail, UserCheck, StickyNote, MessageSquareReply, Edit2,
  ClipboardList, ChevronsRight, ChevronsLeft, Folder, Shield,
  User as UserIcon, CheckCircle2, Inbox, Cog
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import ClasificacionCascadeSelect from './components/ClasificacionCascadeSelect';
import { getSlaState, formatVencimiento } from './components/slaIndicator';
import { AuditTable } from './AuditoriaPage';
import './tickets-theme.css';

const ESTADOS_INFO = {
  NUEVO:      { label: 'Nuevo',      icon: Inbox,         color: 'var(--tk-blue)',   bg: 'var(--tk-blue-soft)',   border: '#bfdbfe' },
  ASIGNADO:   { label: 'Asignado',   icon: UserCheck,     color: 'var(--tk-amber)',  bg: 'var(--tk-amber-soft)',  border: '#fde68a' },
  EN_PROCESO: { label: 'En proceso', icon: Cog,           color: 'var(--tk-violet)', bg: 'var(--tk-violet-soft)', border: '#d8c8f0' },
  TERMINADO:  { label: 'Terminado',  icon: CheckCircle2,  color: 'var(--tk-green)',  bg: 'var(--tk-green-soft)',  border: '#bbf7d0' },
};
const ESTADOS_KEYS = Object.keys(ESTADOS_INFO);

const fmtFecha = (s) => s ? new Date(s).toLocaleString('es-PE') : '—';
const initialsOf = (s) => {
  if (!s) return '?';
  const parts = s.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};
const colorForString = (s) => {
  if (!s) return '#94a3b8';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h}, 45%, 55%)`;
};

export default function TicketDetallePage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [responsables, setResponsables] = useState([]);
  const [prioridades, setPrioridades] = useState([]);
  const [canales, setCanales] = useState([]);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondiendo, setRespondiendo] = useState(false);
  const [respBody, setRespBody] = useState('');
  const [adjuntosDraft, setAdjuntosDraft] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [notaTexto, setNotaTexto] = useState('');
  const [activeTab, setActiveTab] = useState('responder');
  const [editandoClasificacion, setEditandoClasificacion] = useState(false);
  const [auditItems, setAuditItems] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [openAcc, setOpenAcc] = useState({ detalles: true, sla: true, clasificacion: false, asignacion: false, adjuntos: false, auditoria: false });
  const [composerCollapsed, setComposerCollapsed] = useState(() => {
    try { return localStorage.getItem('tk-composer-collapsed') === '1'; }
    catch { return false; }
  });
  const [refreshing, setRefreshing] = useState(false);
  const [pendingNewMessages, setPendingNewMessages] = useState(0); // mensajes ENTRANTES nuevos por polling
  const timelineWrapRef = useRef(null);
  const endOfTimelineRef = useRef(null);
  const initialScrollDoneRef = useRef(false);
  const prevIncomingIdsRef = useRef(null); // Set de IDs entrantes ya conocidos
  const toggleComposer = () => {
    setComposerCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('tk-composer-collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  const fileInputRef = useRef(null);

  const fetchTicket = async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      if (silent) {
        // Refresh silencioso: solo el ticket (catálogos casi estáticos)
        const tRes = await api.get(`/tickets/${ticketId}`);
        const fresh = tRes.data;
        // Detectar mensajes ENTRANTES nuevos
        const newIncoming = (fresh?.mensajes || []).filter(
          (m) => m.direccion === 'ENTRANTE' && !prevIncomingIdsRef.current?.has(m.id)
        );
        if (prevIncomingIdsRef.current !== null && newIncoming.length > 0) {
          setPendingNewMessages((n) => n + newIncoming.length);
          toast.message('Nuevo mensaje recibido', { duration: 4000 });
        }
        // Actualizar set conocido
        const ids = new Set((fresh?.mensajes || []).filter((m) => m.direccion === 'ENTRANTE').map((m) => m.id));
        prevIncomingIdsRef.current = ids;
        setData(fresh);
      } else {
        const [tRes, rRes, pRes, cRes, treeRes] = await Promise.all([
          api.get(`/tickets/${ticketId}`),
          api.get('/tickets/responsables'),
          api.get('/tickets/prioridades'),
          api.get('/tickets/canales'),
          api.get('/tickets/clasificacion/tree'),
        ]);
        setData(tRes.data);
        setResponsables(rRes.data || []);
        setPrioridades(pRes.data || []);
        setCanales(cRes.data || []);
        setTree(treeRes.data || []);
        // Inicializar set de mensajes entrantes conocidos
        prevIncomingIdsRef.current = new Set(
          (tRes.data?.mensajes || [])
            .filter((m) => m.direccion === 'ENTRANTE')
            .map((m) => m.id)
        );
      }
    } catch (e) {
      if (!silent) {
        toast.error(e?.response?.data?.detail || 'Error cargando ticket');
        navigate('/admin/centro-ayuda/tickets');
      }
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  };

  useEffect(() => {
    // Reset refs al cambiar de ticket
    prevIncomingIdsRef.current = null;
    initialScrollDoneRef.current = false;
    setPendingNewMessages(0);
    fetchTicket();
    /* eslint-disable-next-line */
  }, [ticketId]);

  // Auto-refresh cada 15s mientras la pestaña esté activa.
  useEffect(() => {
    if (!ticketId) return undefined;
    const POLL_MS = 15_000;
    let timer = null;
    const tryRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      fetchTicket({ silent: true });
    };
    const start = () => { if (!timer) timer = setInterval(tryRefresh, POLL_MS); };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.visibilityState === 'visible') start(); else stop(); };
    start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line
  }, [ticketId]);

  // Auto-scroll al último mensaje al cargar inicialmente.
  useEffect(() => {
    if (loading || !data || initialScrollDoneRef.current) return;
    // Esperar a que el DOM pinte
    const id = setTimeout(() => {
      const wrap = timelineWrapRef.current;
      if (wrap) {
        wrap.scrollTop = wrap.scrollHeight;
        initialScrollDoneRef.current = true;
      }
    }, 50);
    return () => clearTimeout(id);
  }, [loading, data]);

  const scrollToLatest = () => {
    const wrap = timelineWrapRef.current;
    if (wrap) wrap.scrollTo({ top: wrap.scrollHeight, behavior: 'smooth' });
    setPendingNewMessages(0);
  };

  const patchTicket = async (changes) => {
    try {
      const { data: updated } = await api.patch(`/tickets/${ticketId}`, changes);
      setData((prev) => ({ ...prev, ticket: updated }));
      toast.success('Cambios guardados');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando');
    }
  };

  const guardarClasificacion = async (newId) => {
    try {
      const { data: updated } = await api.patch(`/tickets/${ticketId}`, { clasificacion_id: newId });
      setData((prev) => ({ ...prev, ticket: updated }));
      setEditandoClasificacion(false);
      toast.success(newId ? 'Clasificación actualizada' : 'Clasificación removida');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando clasificación');
    }
  };

  const subirAdjunto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error('El archivo supera 20 MB'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data: adj } = await api.post(`/tickets/${ticketId}/adjuntos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAdjuntosDraft((prev) => [...prev, adj]);
      toast.success('Adjunto cargado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error subiendo adjunto');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const responder = async () => {
    if (!respBody || respBody.trim() === '' || respBody === '<p><br></p>') {
      toast.error('Escriba un mensaje antes de enviar'); return;
    }
    setRespondiendo(true);
    try {
      const { data: result } = await api.post(`/tickets/${ticketId}/responder`, {
        cuerpo_html: respBody,
        adjuntos_ids: adjuntosDraft.map((a) => a.id),
      });
      if (result.ok) {
        toast.success('Respuesta enviada al cliente');
        setRespBody(''); setAdjuntosDraft([]);
        await fetchTicket({ silent: true });
        scrollToLatest();
      } else {
        toast.error(`Error: ${result.error || 'desconocido'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error enviando respuesta');
    } finally { setRespondiendo(false); }
  };

  const guardarNota = async () => {
    if (!notaTexto.trim()) return;
    try {
      await api.post(`/tickets/${ticketId}/nota`, { cuerpo_texto: notaTexto });
      toast.success('Nota interna guardada');
      setNotaTexto('');
      await fetchTicket({ silent: true });
      scrollToLatest();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error guardando nota');
    }
  };

  const verAdjunto = async (adj) => {
    try {
      const { data: r } = await api.get(`/tickets/${ticketId}/adjuntos/${adj.id}/url`);
      window.open(r.url, '_blank', 'noopener');
    } catch { toast.error('No se pudo abrir el adjunto'); }
  };

  const cargarAuditoria = () => {
    if (auditItems !== null) return;
    setAuditLoading(true);
    api.get(`/tickets/${ticketId}/audit`)
      .then((r) => setAuditItems(r.data || []))
      .catch(() => setAuditItems([]))
      .finally(() => setAuditLoading(false));
  };

  if (loading || !data) {
    return (
      <div className="tickets-scope" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'var(--tk-shell-content-h)' }}>
        <Loader2 style={{ width: 32, height: 32, color: 'var(--tk-text-soft)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const t = data.ticket;
  const mensajes = (data.mensajes || []).filter((m) => !(m.direccion === 'SISTEMA' && m.autor_tipo === 'SISTEMA' && m.asunto === '(borrador adjunto)'));
  const pri = prioridades.find((p) => p.id === t.prioridad_id);
  const canal = canales.find((c) => c.id === t.canal_id);
  const resp = responsables.find((r) => r.id === t.responsable_id);
  const sla = getSlaState(t);
  const estadoInfo = ESTADOS_INFO[t.estado] || ESTADOS_INFO.NUEVO;
  const adjuntosTicket = [];
  for (const m of mensajes) for (const a of (m.adjuntos || [])) adjuntosTicket.push(a);

  const slaToneClass =
    sla?.key === 'VENCIDO' ? 'late' :
    sla?.key === 'WARN' ? 'warn' :
    sla?.key === 'OK' ? 'ok' :
    sla?.key === 'CUMPLIDO' ? 'cumplido' :
    sla?.key === 'INCUMPLIDO' ? 'incumplido' : '';

  return (
    <div className="tickets-scope tk-shell tk-shell-flush" style={{ background: 'var(--tk-bg)' }}>
      {/* HEADER */}
      <div className="tk-detalle-header">
        <button className="tk-back-btn" onClick={() => navigate('/admin/centro-ayuda/tickets')} data-testid="btn-volver-kanban" title="Volver al Kanban">
          <ArrowLeft style={{ width: 16, height: 16 }} />
        </button>

        <div className="tk-h-info">
          <div className="tk-h-meta">
            <b>{t.codigo}</b>
            <span>·</span>
            <Mail style={{ width: 12, height: 12 }} />
            <span>{canal?.nombre || 'Correo'}</span>
            {t.clasificacion_ruta && (
              <>
                <span>·</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={t.clasificacion_ruta}>{t.clasificacion_ruta}</span>
              </>
            )}
          </div>
          <h1 className="tk-h-asunto">{t.asunto_original || '(sin asunto)'}</h1>
        </div>

        <div className="tk-h-actions">
          {sla && (
            <span
              className={`tk-sla-rich ${slaToneClass}`}
              title={t.fecha_vencimiento_sla ? `Vence: ${formatVencimiento(t.fecha_vencimiento_sla)}` : sla.label}
              data-testid="sla-badge"
            >
              <span className="tk-dot" />
              {sla.detalle || sla.label}
            </span>
          )}
          <Select value={t.estado} onValueChange={(v) => patchTicket({ estado: v })}>
            <SelectTrigger
              className="h-auto p-0 border-0 focus:ring-0"
              data-testid="select-estado"
              style={{
                background: estadoInfo.bg,
                color: estadoInfo.color,
                border: `1px solid ${estadoInfo.border}`,
                padding: '6px 14px',
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.4px',
                textTransform: 'uppercase',
              }}
            >
              <SelectValue>{estadoInfo.label.toUpperCase()}</SelectValue>
            </SelectTrigger>
            <SelectContent>{ESTADOS_KEYS.map((k) => <SelectItem key={k} value={k}>{ESTADOS_INFO[k].label}</SelectItem>)}</SelectContent>
          </Select>
          <button className="tk-toggle-btn" onClick={() => setSidebarOpen((s) => !s)} title={sidebarOpen ? 'Ocultar panel' : 'Mostrar panel'} data-testid="btn-toggle-sidebar">
            {sidebarOpen ? <ChevronsRight style={{ width: 16, height: 16 }} /> : <ChevronsLeft style={{ width: 16, height: 16 }} />}
          </button>
        </div>
      </div>

      {/* LAYOUT */}
      <div className={`tk-detalle-layout ${sidebarOpen ? '' : 'collapsed'}`}>
        {/* MAIN COL */}
        <div className="tk-main-col">
          <div className="tk-timeline-wrap" data-testid="hilo-mensajes" ref={timelineWrapRef}>
            {mensajes.length === 0 ? (
              <p style={{ fontSize: 14, color: 'var(--tk-text-soft)', fontStyle: 'italic', textAlign: 'center', padding: '40px 0' }}>Sin mensajes en este ticket.</p>
            ) : (
              <div className="tk-timeline">
                {mensajes.map((m) => <TimelineMessage key={m.id} mensaje={m} verAdjunto={verAdjunto} />)}
              </div>
            )}
            {pendingNewMessages > 0 && (
              <button
                type="button"
                className="tk-newmsg-floater"
                onClick={scrollToLatest}
                data-testid="btn-nuevo-mensaje-floater"
                aria-label="Ir al mensaje más reciente"
              >
                <ChevronDown style={{ width: 13, height: 13 }} />
                {pendingNewMessages === 1 ? '1 nuevo mensaje' : `${pendingNewMessages} nuevos mensajes`}
              </button>
            )}
          </div>

          <div className={`tk-composer ${composerCollapsed ? 'is-collapsed' : ''}`}>
            <button
              type="button"
              className="tk-composer-toggle"
              onClick={toggleComposer}
              aria-label={composerCollapsed ? "Expandir composer" : "Minimizar composer"}
              title={composerCollapsed ? "Expandir composer" : "Minimizar composer"}
              data-testid="btn-composer-toggle"
            >
              {composerCollapsed ? <ChevronUp /> : <ChevronDown />}
            </button>

            {composerCollapsed ? (
              <div className="tk-composer-mini" onClick={toggleComposer} data-testid="composer-mini" role="button" tabIndex={0}>
                <span className="tk-composer-mini-tab">
                  {activeTab === 'responder' ? 'Responder al cliente' : activeTab === 'nota' ? 'Nota interna' : 'Ver auditoría'}
                </span>
                <span className="tk-composer-mini-placeholder">
                  {activeTab === 'responder' ? 'Click para escribir respuesta…' : activeTab === 'nota' ? 'Click para agregar una nota interna…' : 'Click para ver la auditoría…'}
                </span>
              </div>
            ) : (
              <>
            <div className="tk-ctabs">
              <button className={`tk-ctab ${activeTab === 'responder' ? 'active' : ''}`} onClick={() => setActiveTab('responder')} data-testid="tab-responder">
                <MessageSquareReply style={{ width: 13, height: 13 }} /> Responder al cliente
              </button>
              <button className={`tk-ctab ${activeTab === 'nota' ? 'active' : ''}`} onClick={() => setActiveTab('nota')} data-testid="tab-nota">
                <StickyNote style={{ width: 13, height: 13 }} /> Nota interna
              </button>
              <button
                className={`tk-ctab ${activeTab === 'auditoria' ? 'active' : ''}`}
                onClick={() => { setActiveTab('auditoria'); cargarAuditoria(); }}
                data-testid="tab-auditoria"
              >
                <ClipboardList style={{ width: 13, height: 13 }} /> Ver auditoría
              </button>
            </div>

            {activeTab === 'responder' && (
              <div>
                <div className="tk-editor-wrap">
                  <ReactQuill
                    value={respBody}
                    onChange={setRespBody}
                    modules={{ toolbar: [[{ header: [false, 2, 3] }], ['bold', 'italic', 'underline'], [{ list: 'ordered' }, { list: 'bullet' }], ['link'], ['clean']] }}
                    placeholder="Escriba su respuesta al cliente…"
                  />
                </div>
                {adjuntosDraft.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {adjuntosDraft.map((a) => (
                      <span key={a.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 8px', background: 'var(--tk-slate-100)', borderRadius: 4, fontSize: 11 }}>
                        <FileText style={{ width: 11, height: 11 }} />
                        {a.nombre}
                        <button onClick={() => setAdjuntosDraft((p) => p.filter((x) => x.id !== a.id))} style={{ background: 'transparent', border: 0, cursor: 'pointer' }}>
                          <X style={{ width: 11, height: 11, color: 'var(--tk-rose)' }} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="tk-composer-row">
                  <div className="tk-ed-actions">
                    <input ref={fileInputRef} type="file" onChange={subirAdjunto} style={{ display: 'none' }} data-testid="input-adjunto" />
                    <button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="btn-adjuntar">
                      {uploading ? <Loader2 style={{ width: 12, height: 12, animation: 'spin 1s linear infinite' }} /> : <Paperclip style={{ width: 12, height: 12 }} />}
                      Adjuntar
                    </button>
                  </div>
                  <button className="tk-btn-primary-lg" onClick={responder} disabled={respondiendo || !respBody} data-testid="btn-enviar-respuesta">
                    {respondiendo ? <Loader2 style={{ width: 13, height: 13, animation: 'spin 1s linear infinite' }} /> : <Send style={{ width: 13, height: 13 }} />}
                    Enviar
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'nota' && (
              <div>
                <Textarea
                  value={notaTexto}
                  onChange={(e) => setNotaTexto(e.target.value)}
                  rows={3}
                  placeholder="Nota interna visible solo para el equipo. No se envía al cliente."
                  className="text-sm"
                  data-testid="textarea-nota"
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                  <button className="tk-btn-primary-lg" onClick={guardarNota} disabled={!notaTexto.trim()} data-testid="btn-guardar-nota">
                    <StickyNote style={{ width: 13, height: 13 }} /> Guardar nota
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'auditoria' && (
              <div style={{ maxHeight: 280, overflow: 'auto' }}>
                <AuditTable items={auditItems || []} loading={auditLoading} hideTicketColumn />
              </div>
            )}
              </>
            )}
          </div>
        </div>

        {/* SIDEBAR */}
        {sidebarOpen && (
          <aside className="tk-side-col" data-testid="sidebar-drawer">
            <StateControl ticket={t} onChange={(estado) => patchTicket({ estado })} />

            <Accordion title="Detalles" icon={UserIcon} open={openAcc.detalles} onToggle={() => setOpenAcc((p) => ({ ...p, detalles: !p.detalles }))} testid="acc-detalles">
              <KV label="Cliente" value={t.cliente_email} />
              <KV label="Canal" value={canal?.nombre || 'Correo'} />
              <KV label="Apertura" value={fmtFecha(t.fecha_apertura)} />
              {t.fecha_asignacion && <KV label="Asignado" value={fmtFecha(t.fecha_asignacion)} />}
              {t.fecha_primera_resp && <KV label="1ra resp." value={fmtFecha(t.fecha_primera_resp)} />}
              {t.fecha_cierre && <KV label="Cierre" value={fmtFecha(t.fecha_cierre)} />}
            </Accordion>

            <Accordion title="SLA" icon={Shield} open={openAcc.sla} onToggle={() => setOpenAcc((p) => ({ ...p, sla: !p.sla }))} testid="acc-sla">
              <SlaBlock ticket={t} prioridad={pri} />
            </Accordion>

            <Accordion title="Clasificación" icon={Folder} open={openAcc.clasificacion} onToggle={() => setOpenAcc((p) => ({ ...p, clasificacion: !p.clasificacion }))} testid="acc-clasificacion">
              <ClasificacionPanel
                ticket={t} tree={tree}
                editando={editandoClasificacion}
                onIniciarEdicion={() => setEditandoClasificacion(true)}
                onCancelar={() => setEditandoClasificacion(false)}
                onConfirmar={guardarClasificacion}
              />
            </Accordion>

            <Accordion title="Asignación" icon={UserCheck} open={openAcc.asignacion} onToggle={() => setOpenAcc((p) => ({ ...p, asignacion: !p.asignacion }))} testid="acc-asignacion">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <div className="tk-label">Responsable</div>
                  <Select value={t.responsable_id || 'unassigned'} onValueChange={(v) => patchTicket({ responsable_id: v === 'unassigned' ? null : v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-responsable"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">— Sin asignar —</SelectItem>
                      {responsables.map((r) => <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {resp && (
                    <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--tk-text-muted)' }}>
                      <span style={{ width: 22, height: 22, borderRadius: '50%', background: colorForString(resp.nombre), color: 'white', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {initialsOf(resp.nombre)}
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{resp.correo}</span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="tk-label">Prioridad</div>
                  <Select value={t.prioridad_id || 'none'} onValueChange={(v) => patchTicket({ prioridad_id: v === 'none' ? null : v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-prioridad">
                      {pri ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: pri.color }} />{pri.nombre}</span> : <SelectValue placeholder="Sin prioridad" />}
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Sin prioridad —</SelectItem>
                      {prioridades.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color }} />
                            {p.nombre} · {p.horas_sla}h
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="tk-label">Canal</div>
                  <Select value={t.canal_id} onValueChange={(v) => patchTicket({ canal_id: v })}>
                    <SelectTrigger className="h-8 text-xs" data-testid="select-canal-detalle"><SelectValue /></SelectTrigger>
                    <SelectContent>{canales.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </Accordion>

            <Accordion title={`Adjuntos · ${adjuntosTicket.length}`} icon={Paperclip} open={openAcc.adjuntos} onToggle={() => setOpenAcc((p) => ({ ...p, adjuntos: !p.adjuntos }))} testid="acc-adjuntos">
              {adjuntosTicket.length === 0 ? (
                <p style={{ fontSize: 11, color: 'var(--tk-text-soft)', fontStyle: 'italic' }}>Sin adjuntos en este ticket.</p>
              ) : adjuntosTicket.map((a) => (
                <button key={a.id} className="tk-file-row" onClick={() => verAdjunto(a)} data-testid={`adj-side-${a.id}`}>
                  <span className="tk-file-icon"><FileText style={{ width: 14, height: 14 }} /></span>
                  <div className="tk-file-meta">
                    <div className="tk-file-name">{a.nombre_original}</div>
                    {a.size_bytes && <div className="tk-file-size">{(a.size_bytes / 1024).toFixed(1)} KB</div>}
                  </div>
                  <Download style={{ width: 13, height: 13, color: 'var(--tk-text-muted)' }} />
                </button>
              ))}
            </Accordion>

            <Accordion
              title="Auditoría"
              icon={ClipboardList}
              open={openAcc.auditoria}
              onToggle={() => {
                setOpenAcc((p) => {
                  if (!p.auditoria) cargarAuditoria();
                  return { ...p, auditoria: !p.auditoria };
                });
              }}
              testid="acc-auditoria"
            >
              <div style={{ fontSize: 11 }}>
                <AuditTable items={auditItems || []} loading={auditLoading} hideTicketColumn />
              </div>
            </Accordion>
          </aside>
        )}
      </div>
    </div>
  );
}

// =====================================================
// SUB-COMPONENTES
// =====================================================

/** Detecta si un HTML viene como documento de email completo (Outlook/Gmail/auto-reply).
 *  Heurística robusta: doctype / <html / <body / ó 2+ <table>. */
function _esEmailCompleto(html) {
  if (!html) return false;
  const s = html.toLowerCase();
  if (s.includes('<!doctype') || s.includes('<html') || s.includes('<body')) return true;
  // Dos o más tablas anidadas → muy probablemente layout email (Outlook style)
  const tableMatches = s.match(/<table\b/g);
  return !!tableMatches && tableMatches.length >= 2;
}

/** Renderiza HTML de email en iframe sandbox aislado, con altura auto-ajustada.
 *  Espejo del comportamiento de Gmail/Outlook web: el email conserva su tipografía,
 *  paddings y tablas sin ser contaminado por el CSS de la app (y viceversa).
 */
function EmailHtmlPreview({ html }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(140);

  // Sanitizar con WHOLE_DOCUMENT para conservar <html>/<head>/<body> intactos.
  // Defensa en profundidad: aunque el sandbox bloquea scripts, prohibimos
  // explícitamente tags/atributos peligrosos.
  const safeHtml = useMemo(() => DOMPurify.sanitize(html || '', {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ['style'],
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
    FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus'],
  }), [html]);

  // Inyectar <base target="_blank"> para que los links del email se abran en pestaña nueva.
  const srcDoc = useMemo(() => {
    if (/<head[^>]*>/i.test(safeHtml)) {
      return safeHtml.replace(/<head([^>]*)>/i, '<head$1><base target="_blank">');
    }
    return `<head><base target="_blank"></head>${safeHtml}`;
  }, [safeHtml]);

  // Auto-resize: medir contentDocument tras load. El email es contenido
  // estático, basta con medir en load + reintentos diferidos para fonts/imágenes.
  // (Evitamos ResizeObserver para no caer en "ResizeObserver loop completed".)
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    let timers = [];
    const measure = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;
        const h = Math.max(
          doc.documentElement.scrollHeight,
          doc.body ? doc.body.scrollHeight : 0
        );
        if (h) setHeight((prev) => (Math.abs(h - prev) > 1 ? h : prev));
      } catch { /* noop */ }
    };

    const onLoad = () => {
      measure();
      // Re-medir tras 100ms y 600ms (cubre fonts custom + imágenes tardías)
      timers.push(setTimeout(measure, 100));
      timers.push(setTimeout(measure, 600));
    };

    iframe.addEventListener('load', onLoad);
    if (iframe.contentDocument?.readyState === 'complete') onLoad();

    return () => {
      iframe.removeEventListener('load', onLoad);
      timers.forEach((t) => clearTimeout(t));
      timers = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [srcDoc]);

  return (
    <div className="tk-tl-email-frame" data-testid="tk-tl-email-frame">
      <iframe
        ref={iframeRef}
        title="Vista del correo"
        srcDoc={srcDoc}
        sandbox="allow-same-origin allow-popups"
        style={{ width: '100%', height: `${height}px`, border: 0, display: 'block' }}
      />
    </div>
  );
}

function TimelineMessage({ mensaje: m, verAdjunto }) {
  const esCliente = m.autor_tipo === 'CLIENTE' || m.direccion === 'ENTRANTE';
  const esSistema = m.direccion === 'SISTEMA';
  const html = m.cuerpo_html || `<p>${(m.cuerpo_texto || '').replace(/\n/g, '<br/>')}</p>`;
  const cleanHtmlInline = DOMPurify.sanitize(html, { ADD_TAGS: ['style'] });
  const isEmail = _esEmailCompleto(m.cuerpo_html);
  const adj = m.adjuntos || [];

  if (esSistema) {
    return (
      <div className="tk-tl-msg sistema">
        <div className="tk-tl-avatar" style={{ background: 'var(--tk-amber)' }}>
          <StickyNote style={{ width: 18, height: 18 }} />
        </div>
        <div className="tk-tl-head">
          <span className="tk-tl-name">Nota interna <span className="tk-tl-mail">· {m.autor_email}</span></span>
          <span className="tk-tl-time">{fmtFecha(m.created_at)}</span>
        </div>
        <div className="tk-tl-body">
          <div dangerouslySetInnerHTML={{ __html: cleanHtmlInline }} />
        </div>
      </div>
    );
  }

  const author = m.autor_email || (esCliente ? 'Cliente' : 'Agente');
  const initials = initialsOf(author);
  const bgColor = esCliente ? '#64748b' : 'var(--tk-primary)';

  return (
    <div className={`tk-tl-msg ${esCliente ? 'cliente' : 'agente'}`}>
      <div className="tk-tl-avatar" style={{ background: bgColor }}>{initials}</div>
      <div className="tk-tl-head">
        <span className="tk-tl-name">
          {author}
          <span style={{ marginLeft: 8 }}>
            <span style={{
              fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 10, letterSpacing: 0.4,
              background: esCliente ? 'var(--tk-slate-100)' : 'var(--tk-primary-soft)',
              color: esCliente ? 'var(--tk-text-muted)' : 'var(--tk-primary)',
            }}>
              {esCliente ? 'CLIENTE' : 'AGENTE'}
            </span>
          </span>
        </span>
        <span className="tk-tl-time">{fmtFecha(m.created_at)}</span>
      </div>
      <div className="tk-tl-body">
        {isEmail ? (
          <EmailHtmlPreview html={m.cuerpo_html} />
        ) : (
          <div className="ql-snow"><div className="ql-editor" style={{ padding: 0, minHeight: 'auto' }} dangerouslySetInnerHTML={{ __html: cleanHtmlInline }} /></div>
        )}
        {adj.length > 0 && (
          <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--tk-border-soft)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {adj.map((a) => (
              <button key={a.id} className="tk-tl-attach" onClick={() => verAdjunto(a)} data-testid={`adj-link-${a.id}`}>
                <FileText style={{ width: 12, height: 12 }} />
                <span>{a.nombre_original}</span>
                <Download style={{ width: 11, height: 11, marginLeft: 4 }} />
                {a.size_bytes && <span style={{ color: 'var(--tk-text-soft)' }}>({(a.size_bytes / 1024).toFixed(1)} KB)</span>}
              </button>
            ))}
          </div>
        )}
        {m.resend_id && m.direccion === 'SALIENTE' && (
          <ResendIdFootnote value={m.resend_id} />
        )}
      </div>
    </div>
  );
}

/** Pie discreto en mensajes SALIENTES con el resend_id (copy + link al panel). */
function ResendIdFootnote({ value }) {
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    try { navigator.clipboard?.writeText(value); } catch { /* noop */ }
  };
  return (
    <div
      className="tk-tl-resend-footnote"
      data-testid="tk-resend-id"
      title="Identificador único de envío en Resend"
    >
      <span>Resend ID:</span>
      <code onClick={handleCopy} className="tk-tl-resend-code" title="Click para copiar">{value}</code>
      <a
        href={`https://resend.com/emails/${value}`}
        target="_blank"
        rel="noopener noreferrer"
        className="tk-tl-resend-link"
        title="Abrir en panel Resend"
      >↗</a>
    </div>
  );
}

function StateControl({ ticket, onChange }) {
  const info = ESTADOS_INFO[ticket.estado] || ESTADOS_INFO.NUEVO;

  return (
    <div className="tk-state-control" data-testid="state-control-panel">
      <div className="tk-state-control-label">Estado actual</div>
      <div className="tk-state-control-current">
        <span className="tk-state-dot-lg" />
        <span className="tk-state-control-name">{info.label.toUpperCase()}</span>
      </div>
      <div className="tk-state-control-actions">
        {['ASIGNADO', 'EN_PROCESO', 'TERMINADO'].map((k) => {
          const inf = ESTADOS_INFO[k];
          const Icon = inf.icon;
          const isCurrent = ticket.estado === k;
          const isSuccess = k === 'TERMINADO';
          const cls = [
            'tk-state-action-btn',
            isCurrent ? 'is-current' : '',
            isSuccess && !isCurrent ? 'is-success' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={k}
              className={cls}
              disabled={isCurrent}
              onClick={() => onChange(k)}
              data-testid={`btn-state-${k}`}
              type="button"
            >
              <Icon />
              {inf.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Accordion({ title, icon: Icon, open, onToggle, children, testid }) {
  return (
    <div className="tk-accordion">
      <button className={`tk-acc-header ${open ? 'open' : ''}`} onClick={onToggle} data-testid={testid}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          {Icon && <Icon style={{ width: 13, height: 13 }} />}
          {title}
        </span>
        <span className="tk-acc-arrow"><ChevronRight style={{ width: 13, height: 13 }} /></span>
      </button>
      <div className={`tk-acc-body ${open ? '' : 'closed'}`}>{children}</div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="tk-kv">
      <span className="tk-k">{label}</span>
      <span className="tk-v" title={value}>{value || '—'}</span>
    </div>
  );
}

function SlaBlock({ ticket: t, prioridad: pri }) {
  const sla = getSlaState(t);
  if (!sla && !t.fecha_vencimiento_sla) {
    return <p style={{ fontSize: 11, color: 'var(--tk-text-soft)', fontStyle: 'italic' }}>Sin SLA configurado (prioridad sin horas).</p>;
  }
  const tone =
    sla?.key === 'VENCIDO' ? 'late' :
    sla?.key === 'WARN' ? 'warn' :
    sla?.key === 'OK' ? 'ok' :
    sla?.key === 'CUMPLIDO' ? 'cumplido' :
    sla?.key === 'INCUMPLIDO' ? 'incumplido' : '';

  // Progreso visual
  // Reglas: % consumido = (total - restante) / total * 100
  //   > 75% queda → Verde   (≤ 25% consumido)
  //   25-75% queda → Verde  (25-75% consumido)
  //   < 25% queda → Amarillo  (>75% consumido)
  //   vencido     → Rojo    (100% + texto "Plazo vencido")
  // En TERMINADO: barra estática 100% verde (cumplido) o roja (incumplido)
  const totales = typeof t.sla_segs_totales === 'number' ? t.sla_segs_totales : null;
  const restantes = typeof t.sla_segs_restantes === 'number' ? t.sla_segs_restantes : null;

  let progressPct = null;
  let progressTone = tone;
  let progressLabel = '';

  if (t.estado === 'TERMINADO') {
    if (t.sla_cumplido === true) {
      progressPct = 100;
      progressTone = 'cumplido';
      progressLabel = '100% · SLA cumplido';
    } else if (t.sla_cumplido === false) {
      progressPct = 100;
      progressTone = 'incumplido';
      progressLabel = '100% · SLA incumplido';
    }
  } else if (totales && totales > 0 && restantes !== null) {
    if (restantes <= 0) {
      progressPct = 100;
      progressTone = 'late';
      progressLabel = 'Plazo vencido';
    } else {
      const consumido = Math.max(0, Math.min(100, ((totales - restantes) / totales) * 100));
      progressPct = consumido;
      // Threshold por porcentaje restante
      const restPct = (restantes / totales) * 100;
      if (restPct < 25) progressTone = 'warn';
      else progressTone = 'ok';
      progressLabel = `${Math.round(consumido)}% del plazo consumido`;
    }
  }

  return (
    <div className={`tk-sla-block ${tone}`}>
      <div className="tk-sla-block-num">{sla?.detalle?.split(' ').slice(0, 2).join(' ') || sla?.label}</div>
      <div className="tk-sla-block-text">
        {t.fecha_vencimiento_sla ? `Vence: ${formatVencimiento(t.fecha_vencimiento_sla)}` : '—'}
        {pri && ` · ${pri.nombre} (${pri.horas_sla}h)`}
      </div>
      {progressPct !== null && (
        <div className="tk-sla-progress-wrap" data-testid="sla-progress-bar">
          <div className={`tk-sla-progress-track tk-sla-tone-${progressTone}`}>
            <div
              className="tk-sla-progress-fill"
              style={{ width: `${progressPct}%` }}
              data-testid="sla-progress-fill"
              data-pct={Math.round(progressPct)}
            />
          </div>
          <div className="tk-sla-progress-label" data-testid="sla-progress-label">
            {progressLabel}
          </div>
        </div>
      )}
    </div>
  );
}

function ClasificacionPanel({ ticket: t, tree, editando, onIniciarEdicion, onCancelar, onConfirmar }) {
  const terminado = t.estado === 'TERMINADO';
  const ruta = t.clasificacion_ruta;
  const tieneNodoActivo = !!t.clasificacion_id;
  const huerfana = !!ruta && !tieneNodoActivo;

  if (editando) return <ClasificacionCascadeSelect tree={tree} value={t.clasificacion_id} onChange={onConfirmar} onCancel={onCancelar} />;
  if (!ruta && !tieneNodoActivo) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, fontStyle: 'italic', color: 'var(--tk-text-soft)' }}>Sin clasificar</span>
        {!terminado && (
          <button className="tk-btn-toolbar" onClick={onIniciarEdicion} data-testid="btn-clasificar" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Edit2 style={{ width: 11, height: 11 }} /> Clasificar
          </button>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ fontSize: 12, lineHeight: 1.4, color: huerfana ? 'var(--tk-text-muted)' : 'var(--tk-text)', fontStyle: huerfana ? 'italic' : 'normal' }} data-testid="clasificacion-snapshot">
        {ruta}
      </div>
      {huerfana && (
        <span style={{ display: 'inline-block', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, background: 'var(--tk-amber-soft)', color: '#78350f', padding: '2px 7px', borderRadius: 4, alignSelf: 'flex-start' }}>
          Original eliminado
        </span>
      )}
      {!terminado && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="tk-btn-toolbar" onClick={onIniciarEdicion} data-testid="btn-reclasificar" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Edit2 style={{ width: 11, height: 11 }} /> {huerfana ? 'Reclasificar' : 'Cambiar'}
          </button>
        </div>
      )}
    </div>
  );
}
